import { Badge, Button, DataTable, type DataTableColumn } from "@design";
import { useQuery } from "@tanstack/react-query";
import { Plus, Users } from "lucide-react";
import { useState } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router";
import { listTeamsOptions, type TeamSummary } from "@/api";
import { EmptyState } from "@/components/build/EmptyState";
import { Text } from "@/components/Text";
import { AdminPage } from "@/features/admin/AdminPage";
import { TeamFormModal } from "./TeamFormModal";

function useColumns(): DataTableColumn<TeamSummary>[] {
  const { t } = useTranslation("teams");
  return [
    {
      key: "name",
      header: t("columns.name"),
      mobile: "primary",
      sortValue: (team) => team.name.toLowerCase(),
      render: (team) => (
        <span className="flex items-center gap-2">
          <Text as="span" variant="body-sm">
            {team.name}
          </Text>
          {team.isEveryone && (
            <Badge size="compact" variant="info">
              {t("everyone")}
            </Badge>
          )}
        </span>
      ),
    },
    {
      key: "members",
      header: t("columns.members"),
      sortValue: (team) => team.memberCount ?? 0,
      render: (team) => (
        <Text as="span" variant="body-sm" tone="secondary" className="whitespace-nowrap">
          {t("memberCount", { count: team.memberCount ?? 0 })}
        </Text>
      ),
    },
    {
      key: "description",
      header: t("columns.description"),
      render: (team) => (
        <Text as="span" variant="body-sm" tone="tertiary" className="line-clamp-2">
          {team.description ?? ""}
        </Text>
      ),
    },
  ];
}

/** /admin/teams: every team, with how many people are in it (MHS-1082). Platform admins create them here. */
export function AdminTeamsPage() {
  const { t } = useTranslation("teams");
  const navigate = useNavigate();
  const teams = useQuery({ ...listTeamsOptions({ query: { page_size: 100 } }), select: (r) => r.data });
  const columns = useColumns();
  const [creating, setCreating] = useState(false);

  return (
    <AdminPage
      title={t("title")}
      subtitle={t("subtitle")}
      actions={
        <Button variant="primary" leadingIcon={<Plus size={16} />} onClick={() => setCreating(true)}>
          {t("new")}
        </Button>
      }
    >
      {teams.isError ? (
        <Text tone="error" role="alert">
          {t("loadFailed")}
        </Text>
      ) : (
        <DataTable
          aria-label={t("tableLabel")}
          columns={columns}
          rows={teams.data ?? []}
          rowKey={(team) => team.uuid}
          loading={teams.isPending}
          onRowClick={(team) => navigate(`/admin/teams/${team.uuid}`)}
          empty={<EmptyState icon={<Users size={32} />} title={t("empty")} body={t("emptyBody")} />}
        />
      )}
      {creating && (
        <TeamFormModal onClose={() => setCreating(false)} onCreated={(uuid) => navigate(`/admin/teams/${uuid}`)} />
      )}
    </AdminPage>
  );
}
